import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Modal from '@components/atoms/modals/modal';
import Button from '@components/atoms/ui-kit/button';
import WizardStepper from '@components/back-office/shared/wizard-stepper';
import type { Connection } from '@utils/types/connections/connection.ts';
import { useGetAllowedPlatforms } from '@utils/api/connections/use-get-allowed-platforms.tsx';
import { useConnectWizard } from './use-connect-wizard';
import StepChoosePlatform from './step-choose-platform';
import StepPermissions from './step-permissions';
import StepAuthorize from './step-authorize';
import StepDefaults from './step-defaults';
import StepTest from './step-test';
import StepDone from './step-done';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** When set, the wizard opens on the authorize step and keeps defaults. */
  reconnectFor?: Connection | null;
}

export default function ConnectWizard({
  isOpen,
  onClose,
  reconnectFor
}: Props) {
  const { t } = useTranslation();
  const { data: allowedPlatforms } = useGetAllowedPlatforms(isOpen);
  const {
    state,
    steps,
    currentStep,
    platformSpec,
    choosePlatform,
    setCredentialField,
    setDefaults,
    runOAuth,
    retryTest,
    handleNext,
    handleBack,
    handleClose,
    canGoNext,
    totalSteps
  } = useConnectWizard({ isOpen, onClose, reconnectFor });

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepChoosePlatform
            value={state.platformId}
            onChange={choosePlatform}
            allowedPlatforms={allowedPlatforms}
          />
        );
      case 2:
        return platformSpec ? (
          <StepPermissions platform={platformSpec} />
        ) : null;
      case 3:
        return platformSpec ? (
          <StepAuthorize
            platform={platformSpec}
            state={state}
            onLaunchOAuth={runOAuth}
            onCredentialChange={setCredentialField}
          />
        ) : null;
      case 4:
        return platformSpec ? (
          <StepDefaults
            platform={platformSpec}
            value={state.defaults}
            onChange={setDefaults}
          />
        ) : null;
      case 5:
        return <StepTest state={state} onRetry={retryTest} />;
      case 6:
        return <StepDone connection={state.finalizedConnection} />;
      default:
        return null;
    }
  };

  const isLastStep = currentStep === totalSteps;

  const primaryLabel = (() => {
    switch (currentStep) {
      case 3:
        if (platformSpec?.authType === 'oauth' && !state.oauthCode) {
          return t('publishing-wizard-authorize-oauth-cta', {
            platform: platformSpec?.displayName ?? ''
          });
        }
        return t('next');
      case 4:
        return t('publishing-wizard-test-cta');
      case 5:
        return t('next');
      case 6:
        return t('publishing-wizard-done-close');
      default:
        return t('next');
    }
  })();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="large"
      title={t('publishing-wizard-title')}
      caption={t('publishing-wizard-subtitle')}
      footerActions={[
        ...(currentStep > 1 && currentStep < totalSteps
          ? [
              <Button
                key="back"
                onClick={handleBack}
                label={t('back')}
                size="small"
                variant="smoke"
                icon={<ChevronLeft className="h-4 w-4" aria-hidden="true" />}
                disabled={state.isFinalizing || state.isAuthorizing}
              />
            ]
          : []),
        <Button
          key="next"
          onClick={handleNext}
          label={primaryLabel}
          size="small"
          variant="primary"
          icon={
            isLastStep ? undefined : (
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            )
          }
          iconPosition="right"
          disabled={!canGoNext || state.isFinalizing}
        />
      ]}
    >
      <div className="pb-2 border-b border-hairline">
        <WizardStepper steps={steps} currentStep={currentStep} />
      </div>

      <div className="max-h-[55vh] overflow-y-auto px-1 pt-2 pb-1">
        {renderStep()}
      </div>
    </Modal>
  );
}
